import React from 'react';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Box, Grid, Button, FormControl, InputLabel, Select, MenuItem,
  Autocomplete, TextField, Typography, Paper, Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, Stack, Tooltip
} from '@mui/material';
import { getRequest, patchRequest, deleteRequest } from 'Includes/api/apicall';
import { GET_URL } from 'Includes/urls';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import OrderDetailsModal from 'Components/OrderDetailsModal';
import {
  loadBillingFilters,
  saveBillingFilters,
} from 'Includes/billingFilterStorage';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';

const FILTER_PAPER_SX = {
  mt: 3,
  p: 2.5,
  borderRadius: 3,
  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
  border: '1px solid',
  borderColor: 'divider',
};

const TABLE_PAPER_SX = {
  mt: 3,
  p: 1.5,
  borderRadius: 3,
  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
  border: '1px solid',
  borderColor: 'divider',
  overflow: 'hidden',
};

const DATA_GRID_SX = {
  border: 'none',
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#f5f7fb',
    fontWeight: 600,
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: '#fafafa',
  },
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid #f0f0f0',
    display: 'flex',
    alignItems: 'center',
  },
};

const SUMMARY_CARD_SX = {
  p: 2,
  borderRadius: 2,
  bgcolor: 'action.hover',
  border: '1px solid',
  borderColor: 'divider',
  height: '100%',
};

const ACTION_BTN_SX = {
  borderRadius: 2,
  textTransform: 'none',
  px: 2,
  py: 1,
};

const uniqueCompanyOptions = (rows, toOption) => {
  const byId = new Map();
  const seenNames = new Set();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const companyId = row?.company != null && row.company !== '' ? String(row.company) : '';
    const companyName = String(row?.company_name || '').trim();
    if (!companyId || !companyName) return;
    const nameKey = companyName.toLowerCase();
    if (byId.has(companyId) || seenNames.has(nameKey)) return;
    byId.set(companyId, toOption(row, companyName));
    seenNames.add(nameKey);
  });
  return Array.from(byId.values());
};

const filterCompanyOptions = (options, { inputValue }) => {
  const query = String(inputValue || '').trim().toLowerCase();
  if (!query) return options;
  return options.filter((option) => String(option?.company_name || '').toLowerCase().includes(query));
};

function CompanyOrderList(props) {
  const navigate = useNavigate();

  const storedFilters = loadBillingFilters();

  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [orderSummary, setOrderSummary] = React.useState({ total_without_tax: 0, tax_amount: 0, total_amount: 0 });
  const [rowCount, setRowCount] = React.useState(0);
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  });

  const [academicYearList, setAcademicYearList] = React.useState([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = React.useState(storedFilters.academicYearId || '');

  const [companies, setCompanies] = React.useState([]);
  const [selectedCompany, setSelectedCompany] = React.useState(null);

  const [digitalServices, setDigitalServices] = React.useState([]);
  const serviceFilterOptions = React.useMemo(() => {
    const base = [
      { type: 'GROUP', id: 'ERP', name: 'ERP' },
      { type: 'GROUP', id: 'ID_CARDS', name: 'ID Cards' },
      { type: 'GROUP', id: 'BIOMETRIC', name: 'Biometric' },
    ];
    const dm = (digitalServices || []).map((d) => ({
      type: 'DIGITAL',
      id: String(d.id),
      name: d.name,
    }));
    return [...base, ...dm];
  }, [digitalServices]);

  const [selectedServiceFilter, setSelectedServiceFilter] = React.useState(() => {
    const t = storedFilters.serviceFilterType;
    const id = storedFilters.serviceFilterId;
    const name = storedFilters.serviceFilterName;
    if (t && id) return { type: t, id: String(id), name: name || '' };
    return null;
  });

  const [inactiveDeadClient, setInactiveDeadClient] = React.useState(false);

  const [detailOpen, setDetailOpen] = React.useState(false);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [detailData, setDetailData] = React.useState(null);

  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const [createDialogYear, setCreateDialogYear] = React.useState('');
  const [createDialogCompanies, setCreateDialogCompanies] = React.useState([]);
  const [createDialogCompany, setCreateDialogCompany] = React.useState(null);

  const fileInputRef = React.useRef(null);
  const [uploadRow, setUploadRow] = React.useState(null);

  const isSuperuser = React.useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return Boolean(u?.is_superuser);
    } catch {
      return false;
    }
  }, []);

  const formatAmount = (v) => {
    const n = parseFloat(v || 0);
    return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const orderHasInvoice = (data) => {
    if (Boolean(data?.has_invoice)) return true;
    if ((data?.invoice_items || []).length > 0) return true;
    const lockingIssueStatuses = new Set(['INVOICED', 'PARTIAL_PAID', 'PAID']);
    return (data?.issue_invoice_items || []).some((it) =>
      lockingIssueStatuses.has(String(it.status || '').toUpperCase())
    );
  };

  const columns = [
    {
      field: 'academic_year',
      headerName: 'Year',
      width: 120,
      valueGetter: () => {
        const year = academicYearList.find((y) => Number(y.id) === Number(selectedAcademicYear));
        return year?.name || '-';
      },
    },
    {
      field: 'company_name',
      headerName: 'Company',
      flex: 1.4,
      minWidth: 180,
      renderCell: (params) => {
        const extra = (params.row.covered_company_names || []).filter(
          (name) => name && name !== params.value
        );
        return (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap title={params.value}>
              {params.value || '-'}
            </Typography>
            {extra.length > 0 ? (
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                title={extra.join(', ')}
              >
                + {extra.length} more: {extra.join(', ')}
              </Typography>
            ) : null}
          </Box>
        );
      },
    },
    {
      field: 'service_name',
      headerName: 'Service',
      flex: 1,
      minWidth: 140,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
          <Chip
            label={params.value || '-'}
            size="small"
            variant="outlined"
            sx={{ maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
          />
          {orderHasInvoice(params.row) ? (
            <Chip label="Invoiced" size="small" color="warning" variant="outlined" sx={{ flexShrink: 0 }} />
          ) : null}
        </Stack>
      ),
    },
    { field: 'order_date', headerName: 'Ordered On', width: 120 },
    {
      field: 'service_duration',
      headerName: 'Duration',
      width: 110,
      renderCell: (params) => {
        const v = params?.row?.service_duration;
        if (v === null || v === undefined || v === '') return '-';
        return `${v} yr${Number(v) === 1 ? '' : 's'}`;
      },
    },
    { field: 'service_end_date', headerName: 'Ends On', width: 120 },
    {
      field: 'item_count',
      headerName: 'Items',
      width: 90,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip label={params.value ?? 0} size="small" color="default" sx={{ minWidth: 36 }} />
      ),
    },
    {
      field: 'tax_amount',
      headerName: 'GST',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatAmount(params.value)}
        </Typography>
      ),
    },
    {
      field: 'total_amount',
      headerName: 'Total',
      width: 140,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatAmount(params.value)}
        </Typography>
      ),
    },
    {
      field: 'order_form',
      headerName: 'Form',
      width: 90,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => {
        const hasForm = Boolean(params?.row?.order_form_details?.file);
        return (
          <Tooltip title={hasForm ? 'Order form uploaded' : 'No form uploaded'}>
            <DescriptionOutlinedIcon
              fontSize="small"
              color={hasForm ? 'success' : 'disabled'}
            />
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      type: 'actions',
      width: 70,
      getActions: (params) => [
        <GridActionsCellItem label="Details" onClick={() => handleViewDetails(params.row)} showInMenu />,
        <GridActionsCellItem label="Edit" onClick={() => handleEditOrder(params.row)} showInMenu />,
        <GridActionsCellItem
          label="Upload Form"
          icon={<UploadFileOutlinedIcon />}
          onClick={() => handleUploadOrderFormClick(params.row)}
          showInMenu
        />,
        <GridActionsCellItem
          label="Download"
          icon={<DownloadOutlinedIcon />}
          onClick={() => handleDownloadOrderForm(params.row)}
          showInMenu
          disabled={!params?.row?.order_form_details?.file}
        />,
        <GridActionsCellItem
          label="Delete"
          onClick={() => handleDeleteOrder(params.row)}
          showInMenu
          disabled={orderHasInvoice(params.row)}
        />,
      ]
    }
  ];

  React.useEffect(() => {
    getAcademicYearList();
    getDigitalServiceList();
  }, []);

  const getAcademicYearList = () => {
    getRequest(GET_URL.academicyear.api, {}, props).then(res => {
      if (res?.status === 200) {
        setAcademicYearList(res.data.data);
      }
    });
  };

  const getDigitalServiceList = () => {
    getRequest(GET_URL.digitalmarketingfeature.api, {}, props).then((res) => {
      if (res?.status === 200) {
        const list = res.data?.data || res.data || [];
        setDigitalServices(Array.isArray(list) ? list : []);
      }
    });
  };

  const getCompanyOptionsForYear = (year) => {
    if (!year) {
      setCompanies([]);
      return;
    }
    getRequest(
      GET_URL.company_billing_config.api,
      { year, limit: 1000, pageno: 1, ...(inactiveDeadClient ? { include_dead_client_only: 1 } : {}) },
      props
    ).then((res) => {
      if (res?.status !== 200) return;
      const data = res.data?.data || [];
      // Unique by company id and by display name — duplicate institute records otherwise repeat in the dropdown.
      const options = uniqueCompanyOptions(data, (row, companyName) => ({
        company: row.company,
        company_name: companyName,
        config_id: row.id,
      }));
      setCompanies(options);
      if (storedFilters.companyId) {
        const preselected = options.find((c) => Number(c.company) === Number(storedFilters.companyId));
        if (preselected) setSelectedCompany(preselected);
      }
    });
  };

  const getDialogCompanyOptions = () => {
    getRequest(
      GET_URL.companysecondarydetails.api,
      { limit: 10000, pageno: 1, ...(inactiveDeadClient ? { include_dead_client_only: 1 } : {}) },
      props
    ).then((res) => {
      if (res?.status !== 200) return;
      const data = res.data?.data || res.data || [];
      setCreateDialogCompanies(uniqueCompanyOptions(data, (row, companyName) => ({
        company: row.company,
        company_name: companyName,
      })));
    });
  };

  const getCompanyOrderList = (year) => {
    const serviceParams = {};
    if (selectedServiceFilter?.type === 'GROUP') {
      serviceParams.service_group = selectedServiceFilter.id;
    } else if (selectedServiceFilter?.type === 'DIGITAL') {
      serviceParams.digitalservice = selectedServiceFilter.id;
    }
    const params = {
      year,
      limit: paginationModel.pageSize,
      pageno: paginationModel.page + 1,
      ...(selectedCompany?.company ? { company: selectedCompany.company } : {}),
      ...serviceParams,
      ...(inactiveDeadClient ? { include_dead_client_only: 1 } : {}),
    };
    setLoading(true);
    getRequest(GET_URL.service_order.api, params, props)
      .then(res => {
        if (res?.status === 200) {
          const data = res.data.data || [];
          setRows(data);
          setRowCount(res.data?.count ?? data.length);
          if (isSuperuser && res.data?.summary) {
            setOrderSummary(res.data.summary);
          } else {
            setOrderSummary({ total_without_tax: 0, tax_amount: 0, total_amount: 0 });
          }
        }
      })
      .finally(() => setLoading(false));
  };

  const handleAcademicYearChange = (e) => {
    const year = e.target.value;
    setSelectedAcademicYear(year);
    setSelectedCompany(null);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    getCompanyOptionsForYear(year);
    if (!year) {
      setRows([]);
      setRowCount(0);
      setOrderSummary({ total_without_tax: 0, tax_amount: 0, total_amount: 0 });
    }
  };

  React.useEffect(() => {
    if (selectedAcademicYear) {
      getCompanyOrderList(selectedAcademicYear);
    }
  }, [selectedAcademicYear, paginationModel.page, paginationModel.pageSize, selectedCompany, selectedServiceFilter, inactiveDeadClient]);

  React.useEffect(() => {
    saveBillingFilters({
      academicYearId: selectedAcademicYear || '',
      companyId: selectedCompany?.company || '',
      serviceFilterType: selectedServiceFilter?.type || '',
      serviceFilterId: selectedServiceFilter?.id || '',
      serviceFilterName: selectedServiceFilter?.name || '',
      includeDeadClientOnly: Boolean(inactiveDeadClient),
    });
  }, [selectedAcademicYear, selectedCompany, selectedServiceFilter, inactiveDeadClient]);

  React.useEffect(() => {
    if (selectedAcademicYear) {
      getCompanyOptionsForYear(selectedAcademicYear);
    }
  }, [inactiveDeadClient]);

  const handleViewDetails = (row) => {
    setDetailOpen(true);
    setDetailLoading(true);
    getRequest(`${GET_URL.service_order.api}${row.id}/`, { view_details: 1 }, props)
      .then(res => setDetailData(res?.data?.data || null))
      .finally(() => setDetailLoading(false));
  };

  const handleEditOrder = (row) => {
    if (!row?.id) return;
    getRequest(`${GET_URL.service_order.api}${row.id}/`, { view_details: 1 }, props).then((res) => {
      const data = res?.data?.data;
      if (!data) {
        Swal.fire('Error', 'Unable to load order details', 'error');
        return;
      }
      // After invoice is issued, edit is still allowed so new services can be added.
      // Invoiced services cannot be deleted (enforced on company-setup + backend).
      const configId = row.billing_config || data?.order?.billing_config;
      if (!configId) {
        Swal.fire('Error', 'Billing config not found for this order', 'error');
        return;
      }
      navigate(`/billing/company-setup?config_id=${configId}`);
    });
  };

  const handleDeleteOrder = (row) => {
    if (!row?.id) return;
    getRequest(`${GET_URL.service_order.api}${row.id}/`, { view_details: 1 }, props).then((res) => {
      const data = res?.data?.data;
      if (!data) {
        Swal.fire('Error', 'Unable to load order details', 'error');
        return;
      }
      if (orderHasInvoice(data)) {
        Swal.fire('Error', 'Invoice created for this order. Cancel the invoice before deleting.', 'error');
        return;
      }
      deleteRequest(`${GET_URL.service_order.api}${row.id}/`, {}, props).then((response) => {
        if (response && response.status === 200) {
          if (selectedAcademicYear) {
            getCompanyOrderList(selectedAcademicYear);
          }
          Swal.fire({
            icon: 'success',
            title: 'Deleted',
            text: 'Order deleted successfully.',
            timer: 1500,
            showConfirmButton: false,
          });
        }
      });
    });
  };

  const handleUploadOrderFormClick = (row) => {
    setUploadRow(row);
    fileInputRef.current.click();
  };

  const handleOrderFormFileSelected = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!uploadRow?.id) {
      Swal.fire('Select order', 'Please select an order before uploading', 'warning');
      e.target.value = '';
      return;
    }

    const fd = new FormData();
    fd.append('file', file);

    patchRequest(`${GET_URL.service_order.api}${uploadRow.id}/?upload_order_form=1`, fd, props)
      .then(() => {
        Swal.fire('Success', 'Uploaded', 'success');
        if (selectedAcademicYear) {
          getCompanyOrderList(selectedAcademicYear);
        }
      });
    e.target.value = '';
  };

  const handleDownloadOrderForm = (row) => {
    if (row?.order_form_details?.file) {
      window.open(row.order_form_details.file);
    }
  };

  const handleExport = () => {
    if (!selectedAcademicYear) {
      Swal.fire('Select year', 'Please select an academic year first', 'warning');
      return;
    }
    const serviceParams = {};
    if (selectedServiceFilter?.type === 'GROUP') {
      serviceParams.service_group = selectedServiceFilter.id;
    } else if (selectedServiceFilter?.type === 'DIGITAL') {
      serviceParams.digitalservice = selectedServiceFilter.id;
    }
    const params = {
      year: selectedAcademicYear,
      ...(selectedCompany?.company ? { company: selectedCompany.company } : {}),
      ...serviceParams,
      ...(inactiveDeadClient ? { include_dead_client_only: 1 } : {}),
    };
    const url = GET_URL.service_order.api;
    const reqProps = { ...props, responseType: 'blob' };
    getRequest(url, { ...params, export: 1 }, reqProps).then((response) => {
      if (response && response.status === 200) {
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
        const fileURL = window.URL.createObjectURL(blob);
        const contentDisposition =
          response?.headers?.['content-disposition'] || response?.headers?.['Content-Disposition'];
        const match = typeof contentDisposition === 'string'
          ? contentDisposition.match(/filename="?([^"]+)"?/i)
          : null;
        const filename = match?.[1] || `company_orders_year_${selectedAcademicYear}.csv`;
        const link = document.createElement('a');
        link.href = fileURL;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(fileURL);
        document.body.removeChild(link);
      }
    });
  };

  const handleCreateOrder = () => {
    const year = selectedAcademicYear || '';
    setCreateDialogYear(year);
    setCreateDialogCompany(selectedCompany || null);
    setCreateDialogOpen(true);
    getDialogCompanyOptions();
  };

  const handleCreateDialogContinue = () => {
    if (!createDialogYear) {
      Swal.fire('Select year', 'Please select an academic year first', 'warning');
      return;
    }
    if (!createDialogCompany?.company) {
      Swal.fire('Select company', 'Please select a company first', 'warning');
      return;
    }

    getRequest(
      GET_URL.company_billing_config.api,
      { company: createDialogCompany.company, year: createDialogYear, limit: 1, pageno: 1 },
      props
    ).then((res) => {
      const cfg = res?.data?.data?.[0];
      setCreateDialogOpen(false);
      if (cfg?.id) {
        navigate(`/billing/company-setup?config_id=${cfg.id}`);
      } else {
        Swal.fire(
          'Billing setup required',
          'This company has no billing setup for the selected year. Create a billing setup first before creating an order.',
          'warning'
        );
      }
    });
  };

  React.useEffect(() => {
    if (!academicYearList.length || !storedFilters.academicYearId) return;
    const exists = academicYearList.some((y) => Number(y.id) === Number(storedFilters.academicYearId));
    if (!exists) return;
    setSelectedAcademicYear(storedFilters.academicYearId);
    if (storedFilters.includeDeadClientOnly) {
      setInactiveDeadClient(true);
    }
    getCompanyOptionsForYear(storedFilters.academicYearId);
  }, [academicYearList]);

  return (
    <Box sx={{ p: 3 }}>

      <input ref={fileInputRef} type="file" hidden onChange={handleOrderFormFileSelected} />

      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography variant="h5" fontWeight={600}>
            Company Orders
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Filter, review, and manage service orders by year and company.
          </Typography>
        </Grid>

        <Grid item xs={12} md={6} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
          <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} flexWrap="wrap" useFlexGap>
            {isSuperuser && (
              <Button
                variant="outlined"
                onClick={handleExport}
                startIcon={<DownloadOutlinedIcon />}
                disabled={!selectedAcademicYear}
                sx={ACTION_BTN_SX}
              >
                Export
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleCreateOrder}
              startIcon={<AddCircleOutlineOutlinedIcon />}
              sx={{ ...ACTION_BTN_SX, boxShadow: '0 4px 14px rgba(0,0,0,0.1)' }}
            >
              Create Order
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/billing/company-setup/bulk-orders')}
              startIcon={<AddCircleOutlineOutlinedIcon />}
              sx={ACTION_BTN_SX}
            >
              Create Bulk Orders
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <Paper sx={FILTER_PAPER_SX}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Academic Year</InputLabel>
              <Select
                value={selectedAcademicYear}
                label="Academic Year"
                onChange={handleAcademicYearChange}
              >
                {academicYearList.map(y => (
                  <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              options={companies}
              getOptionLabel={(o) => o.company_name || ''}
              getOptionKey={(o) => String(o?.company)}
              isOptionEqualToValue={(option, value) => String(option?.company) === String(value?.company)}
              filterOptions={filterCompanyOptions}
              value={selectedCompany}
              onChange={(_e, value) => {
                setSelectedCompany(value || null);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
              renderInput={(params) => <TextField {...params} label="Company" size="small" />}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              options={serviceFilterOptions}
              getOptionLabel={(o) => o.name || ''}
              value={selectedServiceFilter}
              onChange={(_e, value) => {
                setSelectedServiceFilter(value || null);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
              renderInput={(params) => <TextField {...params} label="Service" size="small" />}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Client Type</InputLabel>
              <Select
                value={inactiveDeadClient ? 'dead' : 'active'}
                label="Client Type"
                onChange={(e) => {
                  setInactiveDeadClient(e.target.value === 'dead');
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="dead">Inactive / Dead</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              Showing {rows.length} of {rowCount} order{rowCount === 1 ? '' : 's'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {isSuperuser && selectedAcademicYear && (
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={4}>
            <Box sx={SUMMARY_CARD_SX}>
              <Typography variant="caption" color="text.secondary" display="block">
                Base (excl. tax)
              </Typography>
              <Typography variant="h6" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatAmount(orderSummary.total_without_tax)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={SUMMARY_CARD_SX}>
              <Typography variant="caption" color="text.secondary" display="block">
                GST
              </Typography>
              <Typography variant="h6" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatAmount(orderSummary.tax_amount)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                ...SUMMARY_CARD_SX,
                bgcolor: 'primary.main',
                borderColor: 'primary.main',
                color: 'primary.contrastText',
              }}
            >
              <Typography variant="caption" sx={{ opacity: 0.85 }} display="block">
                Total
              </Typography>
              <Typography variant="h6" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatAmount(orderSummary.total_amount)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      )}

      <Paper sx={TABLE_PAPER_SX}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pagination
          paginationMode="server"
          page={paginationModel.page}
          pageSize={paginationModel.pageSize}
          onPageChange={(newPage) => setPaginationModel((prev) => ({ ...prev, page: newPage }))}
          onPageSizeChange={(newSize) => setPaginationModel({ page: 0, pageSize: newSize })}
          rowsPerPageOptions={[10, 20, 50]}
          rowCount={rowCount}
          autoHeight
          rowHeight={48}
          disableSelectionOnClick
          sx={DATA_GRID_SX}
        />
      </Paper>

      <OrderDetailsModal
        open={detailOpen}
        loading={detailLoading}
        data={detailData}
        onClose={() => setDetailOpen(false)}
      />

      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>Create Order</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pick the academic year and company to continue to billing setup.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Academic Year</InputLabel>
                <Select
                  value={createDialogYear}
                  label="Academic Year"
                  onChange={(e) => {
                    const year = e.target.value;
                    setCreateDialogYear(year);
                  }}
                >
                  {academicYearList.map((y) => (
                    <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                options={createDialogCompanies}
                getOptionLabel={(o) => o.company_name || ''}
                getOptionKey={(o) => String(o?.company)}
                isOptionEqualToValue={(option, value) => String(option?.company) === String(value?.company)}
                filterOptions={filterCompanyOptions}
                value={createDialogCompany}
                onChange={(_e, value) => setCreateDialogCompany(value || null)}
                renderInput={(params) => <TextField {...params} label="Company" size="small" />}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateDialogOpen(false)} color="inherit" sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button onClick={handleCreateDialogContinue} variant="contained" sx={ACTION_BTN_SX}>
            Continue
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default CompanyOrderList;